import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Check, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useScrollReveal } from "@/hooks/use-scroll-reveal";

const packages = [
  {
    name: "Basic Package",
    price: "R300",
    period: "p/m",
    turnover: "Nil Turnover",
    popular: false,
    features: [
      "Nil Annual Tax Return",
      "2x Nil Provisional Tax Return",
      "CIPC Annual Return (Nil Turnover)",
      "Beneficial Ownership",
    ],
  },
  {
    name: "Entry Level Package",
    price: "R2,500",
    period: "p/m",
    turnover: "Up to R2.3m Turnover",
    popular: true,
    features: [
      "Monthly Bookkeeping (60 entries)",
      "Payroll 1-5 Employees",
      "EMP201 & EMP501",
      "UIF Monthly Declaration",
      "Annual Financial Statements",
      "Tax Returns & CIPC",
    ],
  },
  {
    name: "Medium Level Package",
    price: "R4,500",
    period: "p/m",
    turnover: "Up to R12m Turnover",
    popular: false,
    features: [
      "Monthly Bookkeeping (120 entries)",
      "VAT201 Monthly Declaration",
      "Payroll 1-10 Employees",
      "EMP201 & EMP501",
      "Workmen's Compensation",
      "Annual Financial Statements",
      "Full Compliance Package",
    ],
  },
];

export function PackagesPreview() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section className="py-20 lg:py-28 relative overflow-hidden">
      {/* Subtle pattern background */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)`,
        backgroundSize: '24px 24px'
      }} />
      <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/30 to-background" />

      <div className="container mx-auto px-4 relative z-10">
        <SectionHeader
          label="Packages"
          title="Transparent Pricing, No Surprises"
          description="Choose the package that fits your business. All-inclusive monthly fees with no hidden costs."
        />

        <div
          ref={ref}
          className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto reveal-stagger"
        >
          {packages.map((pkg) => (
            <div
              key={pkg.name}
              className={cn(
                "p-8 flex flex-col scroll-reveal-scale rounded-xl border bg-card transition-all duration-300",
                isVisible && "revealed",
                pkg.popular
                  ? "card-popular-glow scale-[1.02] hover:scale-[1.04]"
                  : "card-elevated"
              )}
            >
              {/* Popular badge */}
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-4 py-1 rounded-full">
                  Most Popular
                </div>
              )}

              {/* Package Header */}
              <div className="text-center mb-6">
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                  {pkg.name}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">{pkg.turnover}</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className={cn("text-4xl font-bold", pkg.popular ? "text-gradient" : "text-primary")} style={pkg.popular ? { WebkitTextFillColor: 'transparent' } : {}}>
                    {pkg.price}
                  </span>
                  <span className="price-period">{pkg.period}</span>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-border my-6" />

              {/* Features */}
              <ul className="space-y-3 flex-1 mb-8">
                {pkg.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-sm">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="space-y-3">
                <Link to={`/packages?enquire=${encodeURIComponent(pkg.name)}`} className="block">
                  <Button
                    className={cn(
                      "w-full",
                      pkg.popular ? "btn-primary btn-shimmer" : "btn-outline-primary"
                    )}
                  >
                    Enquire Now
                  </Button>
                </Link>
                <Link to="/packages" className="block">
                  <Button variant="ghost" className="w-full text-muted-foreground hover:text-primary">
                    See Full Details
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
